/**
 * Afuna Aromatics — Ritual R1 Experience workspace.
 *
 * R3 founder-review fix (single source of truth):
 * One function, setPathway(state), is the ONLY thing that changes active
 * content. It drives, together, in one call:
 *   - the three native radios (kept for no-JS fallback + keyboard state),
 *   - [data-active-pathway] on the section root (CSS keys the active pill,
 *     pathway image, heading, blend pair, gateway ring off this attribute),
 *   - the intention question + affirmation text (in-memory cycling),
 *   - the polite screen-reader announcement,
 *   - an OPTIONAL smooth scroll to the start of the pathway content, only
 *     when the customer's action asked for it (a gateway tap), never on load.
 *
 * The previous build let CSS :checked ~ sibling selectors drive the visual
 * state while JS only updated the question — two systems that could fall out
 * of sync (iOS Safari not repainting :checked ~ after a programmatic
 * radio.checked, leaving e.g. the Remember image visible under a Reign
 * selection). The :checked ~ rules are now the no-JS fallback only
 * (:not([data-active-pathway])).
 *
 * Progressive enhancement: with JS disabled the native radio/label
 * semantics + the gated :checked ~ rules keep the whole workspace working,
 * Release checked by default.
 *
 * Question cycling is in-memory only — never a cookie, sessionStorage, or
 * localStorage. Reduced motion uses instant movement.
 */
(function () {
  var STATES = ['release', 'remember', 'reign'];

  function enhance(root) {
    var radios = root.querySelectorAll('.aa-ritual-workspace__radio');
    if (!radios.length) return;

    var workspaceStart = root.querySelector('[data-workspace-start]');
    var reduceMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');

    /* ---------- polite screen-reader region (one) ---------- */
    var live = document.createElement('div');
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('role', 'status');
    live.style.cssText =
      'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;';
    root.appendChild(live);

    /* ---------- intention-question cycling (in-memory only) ---------- */
    var visitCounts = { release: 0, remember: 0, reign: 0 };

    function questionCountFor(state) {
      var data = root.querySelector('[data-reflection-state="' + state + '"]');
      return data ? data.querySelectorAll('[data-q]').length : 0;
    }
    function questionIndexFor(state) {
      var total = questionCountFor(state);
      if (total < 1) return 1;
      var count = visitCounts[state] || 1;
      return ((count - 1) % total) + 1;
    }
    function questionText(state, index) {
      var data = root.querySelector('[data-reflection-state="' + state + '"]');
      if (!data) return '';
      var q = data.querySelector('[data-q="' + index + '"]');
      return q ? q.textContent.trim() : '';
    }
    function applyQuestion(state) {
      var text = questionText(state, questionIndexFor(state));
      root
        .querySelectorAll('[data-question-text][data-state="' + state + '"]')
        .forEach(function (el) {
          if (text) el.textContent = text;
        });
      var affTargets = root.querySelectorAll('[data-affirmation-text][data-state="' + state + '"]');
      return {
        question: text,
        affirmation: affTargets.length ? affTargets[0].textContent.trim() : '',
      };
    }

    function moveToWorkspace(state) {
      if (!workspaceStart) return;
      workspaceStart.scrollIntoView({
        behavior: reduceMotionMQ.matches ? 'auto' : 'smooth',
        block: 'start',
      });
      var heading = root.querySelector('.aa-ritual-workspace__heading--' + state);
      if (heading) {
        window.requestAnimationFrame(function () {
          heading.focus({ preventScroll: true });
        });
      }
    }

    /* R4 item 1: the mobile gateway is a horizontal scroll-snap strip.
       When a pathway becomes active from any control, bring that pathway's
       gateway panel into the strip's own view so the strip can never park
       on a different pathway than the one that is selected. Horizontal
       scroll on the strip only — never the page, never on initial load. */
    var gatewayStrip = root.querySelector('.aa-ritual-workspace__gateway-mobile');
    function syncGatewayStrip(state) {
      if (!gatewayStrip) return;
      var panel = gatewayStrip.querySelector('.aa-ritual-workspace__gateway-panel--' + state);
      if (!panel) return;
      var left = panel.offsetLeft - (gatewayStrip.clientWidth - panel.clientWidth) / 2;
      if (left < 0) left = 0;
      try {
        gatewayStrip.scrollTo({
          left: left,
          behavior: reduceMotionMQ.matches ? 'auto' : 'smooth',
        });
      } catch (e) {
        gatewayStrip.scrollLeft = left;
      }
    }

    var current = (function () {
      for (var i = 0; i < radios.length; i++) {
        if (radios[i].checked) return radios[i].value;
      }
      return 'release';
    })();

    /* ---------- THE single source of truth ---------- */
    function setPathway(state, opts) {
      opts = opts || {};
      if (STATES.indexOf(state) === -1) return;
      var changed = state !== current || opts.force === true;
      current = state;

      // 1. radios (no-JS fallback + keyboard state)
      radios.forEach(function (r) {
        if (r.value === state) {
          if (!r.checked) r.checked = true;
        } else if (r.checked) {
          r.checked = false;
        }
      });

      // 2. the authoritative visual attribute (CSS keys the active pill,
      //    pathway image, heading, blend pair, and gateway ring off this)
      root.setAttribute('data-active-pathway', state);

      // 3. intention question + affirmation
      if (changed && opts.force !== true) {
        visitCounts[state] = (visitCounts[state] || 0) + 1;
      }
      var applied = applyQuestion(state);

      // 4. announce (only on a real change)
      if (changed && opts.force !== true) {
        live.textContent =
          'Ritual pathway: ' + state + '. Intention question: ' + applied.question +
          '. Today I affirm: ' + applied.affirmation;
      }

      // 5. keep the horizontal mobile gateway strip on the active pathway
      //    (never on the initial forced sync)
      if (opts.force !== true) syncGatewayStrip(state);

      // 6. optional explicit scroll — only when a customer action asked for it
      if (opts.scroll === true) moveToWorkspace(state);
    }

    /* ---------- label taps: pills, gateway panels, triptych regions ---------- */
    root
      .querySelectorAll(
        '.aa-ritual-workspace__tab, .aa-ritual-workspace__triptych-region, .aa-ritual-workspace__gateway-panel'
      )
      .forEach(function (label) {
        label.addEventListener('click', function (event) {
          var forId = label.getAttribute('for');
          var radio = forId ? document.getElementById(forId) : null;
          var state = radio ? radio.value : null;
          if (!state) return;
          // stop the native label -> input activation (which would scroll to the
          // visually-hidden radio); we set state ourselves.
          event.preventDefault();
          setPathway(state, { scroll: label.hasAttribute('data-scroll-to-workspace') });
        });
      });

    /* ---------- keyboard: arrow-key nav on the radio group ---------- */
    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (radio.checked) setPathway(radio.value, { scroll: false });
      });
    });

    /* ---------- image-tap jump (preserved) ---------- */
    root.querySelectorAll('[data-jump-intention]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var artwork = btn.closest('.aa-ritual-workspace__artwork-mobile');
        var overlay = artwork && artwork.querySelector('.aa-ritual-workspace__overlay');
        if (!overlay) return;
        overlay.scrollIntoView({
          behavior: reduceMotionMQ.matches ? 'auto' : 'smooth',
          block: 'start',
        });
        var question = overlay.querySelector('.aa-ritual-workspace__overlay-question');
        if (question) {
          question.setAttribute('tabindex', '-1');
          window.requestAnimationFrame(function () {
            question.focus({ preventScroll: true });
          });
        }
      });
    });

    /* ---------- init: no scroll, no announcement, seed the first question ---------- */
    setPathway(current, { force: true });
    visitCounts[current] = 1;
  }

  document.querySelectorAll('.aa-ritual-workspace').forEach(enhance);
})();
